import { useContext, useMemo } from 'react';

import { SessionContext } from '@/shared/session';

export type MerchantPortalNavFeatureKey =
  | 'store'
  | 'team'
  | 'orders'
  | 'customers'
  | 'media'
  | 'pricing'
  | 'loyalty'
  | 'inventory'
  | 'promo'
  | 'publication'
  | 'channelStock'
  | 'conversations';

const NAV_FEATURE_FLAG_KEYS: Record<MerchantPortalNavFeatureKey, string> = {
  store: 'merchantPortal.nav.store',
  team: 'merchantPortal.nav.team',
  orders: 'merchantPortal.nav.orders',
  customers: 'merchantPortal.nav.customers',
  media: 'merchantPortal.nav.media',
  pricing: 'merchantPortal.nav.pricing',
  loyalty: 'merchantPortal.nav.loyalty',
  inventory: 'merchantPortal.nav.inventory',
  promo: 'merchantPortal.nav.promo',
  channelStock: 'merchantPortal.nav.channelStock',
  publication: 'merchantPortal.nav.publication',
  conversations: 'merchantPortal.nav.conversations',
};

const DEFAULT_NAV_VISIBILITY: Record<MerchantPortalNavFeatureKey, boolean> = {
  store: true,
  // plan-platform-001 WP2.3 — shipped, not staged (design-stock-channels-page §5); tenants can be
  // staged off during the Phase-2 rollout via merchantPortal.nav.channelStock.
  channelStock: true,
  // The Users workspace (users, groups, access, self-service) ships enabled, matching the
  // "shipped, not staged" entries below: it is permission-gated on `employees.view`/`employees.manage`,
  // so staff with neither never see it, and hiding it by default would leave merchants unable to grant
  // access at all. A session feature flag can still stage it back off per tenant.
  team: true,
  orders: false,
  customers: true,
  media: false,
  // Pricing ("Цены", user-story-006), Promo ("Промо", user-story-022) and Publication
  // (user-story-merchant-portal-034/035/036) are fully implemented, permission-gated workspaces —
  // enabled by default. A session-provided featureFlags entry can still stage any of them back off
  // (e.g. a tenant not yet ready for it) via the mechanism below.
  pricing: true,
  loyalty: false,
  inventory: true,
  promo: true,
  publication: true,
  // The omnichannel conversation inbox (plan-conversation-001). Registered in WP-MP.0 alongside its
  // permission/registry plumbing; the screen itself shipped in plan-merchant-portal-019 WP3 (=
  // WP-MP.5) and is reachable today by anyone holding `conversations.view` regardless of this flag
  // (`RequireSection` ignores staging, adr-auth-006 D6) — this only gates the sidebar entry. Stays
  // off by default for the pilot (adr-merchant-portal-017); a tenant pilot can flip it on the same
  // way `orders`/`loyalty` are staged today.
  conversations: false,
};

export type MerchantPortalSettingsSectionKey =
  | 'salesChannels'
  | 'customersMarketing'
  | 'selling'
  | 'fulfilment'
  | 'catalogMedia'
  | 'chatbot';

const SETTINGS_SECTION_FLAG_KEYS: Record<MerchantPortalSettingsSectionKey, string> = {
  // The `ai` group and its `merchantPortal.settings.ai` flag were retired in plan-merchant-portal-019
  // WP2: the AI provider card moved into `chatbot` below and `/settings/ai-agents` is now a redirect
  // — see the as-is inventory in plan-conversation-001's "Workstream — apps/merchant-portal" section.
  salesChannels: 'merchantPortal.settings.salesChannels',
  customersMarketing: 'merchantPortal.settings.customersMarketing',
  selling: 'merchantPortal.settings.selling',
  fulfilment: 'merchantPortal.settings.fulfilment',
  catalogMedia: 'merchantPortal.settings.catalogMedia',
  chatbot: 'merchantPortal.settings.chatbot',
};

const DEFAULT_SETTINGS_SECTION_VISIBILITY: Record<MerchantPortalSettingsSectionKey, boolean> = {
  salesChannels: true,
  customersMarketing: false,
  selling: false,
  fulfilment: false,
  catalogMedia: false,
  // The chatbot rail group (plan-conversation-001). Registered in WP-MP.0 with no page behind it;
  // WP-MP.2 gives it its first row — the templates & FAQ editor, `content.*`-gated — so the group is
  // now "shipped, not staged": staying off would hide the only editor of the bot's texts from every
  // tenant. The `chatbot` *behaviour* domain (`settings.chatbot.*`) still ships in WP-MP.4 and still
  // sits in `SETTINGS_CODES_WITHOUT_PAGES` (`shared/navigation/appSections.ts`) until then — that is
  // independent of this group-level flag, which only gates whether the group appears at all.
  chatbot: true,
};

function resolveFeatureFlag(
  featureFlags: Record<string, boolean> | null | undefined,
  key: MerchantPortalNavFeatureKey,
): boolean {
  const value = featureFlags?.[NAV_FEATURE_FLAG_KEYS[key]];
  return typeof value === 'boolean' ? value : DEFAULT_NAV_VISIBILITY[key];
}

/**
 * Central switch for sidebar nav visibility.
 *
 * The hidden sections default to off, but each one can be turned back on via a
 * session-provided `featureFlags` entry. That keeps the menu easy to gate now
 * and easy to externalise later without touching the shell again.
 */
export function resolveMerchantPortalNavVisibility(
  featureFlags?: Record<string, boolean> | null,
): Record<MerchantPortalNavFeatureKey, boolean> {
  return {
    store: resolveFeatureFlag(featureFlags, 'store'),
    channelStock: resolveFeatureFlag(featureFlags, 'channelStock'),
    team: resolveFeatureFlag(featureFlags, 'team'),
    orders: resolveFeatureFlag(featureFlags, 'orders'),
    customers: resolveFeatureFlag(featureFlags, 'customers'),
    media: resolveFeatureFlag(featureFlags, 'media'),
    pricing: resolveFeatureFlag(featureFlags, 'pricing'),
    loyalty: resolveFeatureFlag(featureFlags, 'loyalty'),
    inventory: resolveFeatureFlag(featureFlags, 'inventory'),
    promo: resolveFeatureFlag(featureFlags, 'promo'),
    publication: resolveFeatureFlag(featureFlags, 'publication'),
    conversations: resolveFeatureFlag(featureFlags, 'conversations'),
  };
}

/**
 * Reads the current sidebar visibility toggles from the session projection.
 *
 * <p>Reads the context directly rather than through `useSession()`, which throws when no provider is
 * above it. Visibility is now consulted by the route guards as well as the sidebar, and the defaults
 * already *are* the answer for "no projection available" — a missing flag and a missing session are
 * the same question. Crashing the guarded subtree instead of falling back would turn a provider-order
 * mistake into a blank application, which is a strictly worse failure than a menu at its defaults.
 */
export function useMerchantPortalNavVisibility(): Record<MerchantPortalNavFeatureKey, boolean> {
  const featureFlags = useContext(SessionContext)?.featureFlags;
  return useMemo(() => resolveMerchantPortalNavVisibility(featureFlags), [featureFlags]);
}

function resolveSettingsSectionFlag(
  featureFlags: Record<string, boolean> | null | undefined,
  key: MerchantPortalSettingsSectionKey,
): boolean {
  const value = featureFlags?.[SETTINGS_SECTION_FLAG_KEYS[key]];
  return typeof value === 'boolean' ? value : DEFAULT_SETTINGS_SECTION_VISIBILITY[key];
}

/** Reads the current settings-section visibility toggles from the session projection. */
export function resolveMerchantPortalSettingsSectionVisibility(
  featureFlags?: Record<string, boolean> | null,
): Record<MerchantPortalSettingsSectionKey, boolean> {
  return {
    salesChannels: resolveSettingsSectionFlag(featureFlags, 'salesChannels'),
    customersMarketing: resolveSettingsSectionFlag(featureFlags, 'customersMarketing'),
    selling: resolveSettingsSectionFlag(featureFlags, 'selling'),
    fulfilment: resolveSettingsSectionFlag(featureFlags, 'fulfilment'),
    catalogMedia: resolveSettingsSectionFlag(featureFlags, 'catalogMedia'),
    chatbot: resolveSettingsSectionFlag(featureFlags, 'chatbot'),
  };
}

/** Reads the current settings-section visibility toggles from the session projection. */
export function useMerchantPortalSettingsSectionVisibility(): Record<
  MerchantPortalSettingsSectionKey,
  boolean
> {
  const featureFlags = useContext(SessionContext)?.featureFlags;
  return useMemo(
    () => resolveMerchantPortalSettingsSectionVisibility(featureFlags),
    [featureFlags],
  );
}
